import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

function App() {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Welcome message
  useEffect(() => {
    setMessages([
      { id: 1, text: "Hello, I'm Liya, your AI assistant Developed by Rubitcube", sender: 'bot' },
      { id: 2, text: 'How can I help you?', sender: 'bot' },
    ]);
  }, []);

  // Fetch chatbot response from the backend
  const fetchChatbotResponse = async (question) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      return data.answer;
    } catch (error) {
      console.error('Error fetching chatbot response:', error);
      setError('An error occurred while fetching the response.');
    } finally {
      setLoading(false);
    }
  };

  // Handle sending a message
  const handleSendMessage = async () => {
    if (!inputText.trim()) return;

    // Add user message to the chat
    const userMessage = { id: messages.length + 1, text: inputText, sender: 'user' };
    setMessages((prevMessages) => [...prevMessages, userMessage]);

    // Clear input box immediately
    setInputText('');

    // Show "Liya is thinking..." animation
    setMessages((prevMessages) => [
      ...prevMessages,
      { id: prevMessages.length + 1, text: 'Liya is thinking', sender: 'bot', typing: true },
    ]);

    // Simulate thinking delay
    await new Promise((resolve) => setTimeout(resolve, 2000)); // 2 seconds delay

    // Replace "Liya is thinking..." with "Liya is typing..." animation
    setMessages((prevMessages) => [
      ...prevMessages.slice(0, -1),
      { id: prevMessages.length, text: 'Liya is typing', sender: 'bot', typing: true },
    ]);

    // Fetch chatbot response
    const answer = await fetchChatbotResponse(inputText);
    if (answer) {
      // Add bot response letter by letter
      let typedText = '';
      for (let i = 0; i < answer.length; i++) {
        typedText += answer[i];
        setMessages((prevMessages) => [
          ...prevMessages.slice(0, -1),
          { ...prevMessages[prevMessages.length - 1], text: typedText, typing: false },
        ]);
        await new Promise((resolve) => setTimeout(resolve, 20)); // Typing speed
      }
    }
  };

  // Website Input Component (Hidden Route)
  const WebsiteInput = () => {
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [message, setMessage] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();

      if (!websiteUrl) {
        setMessage('Please enter a website URL.');
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/set-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ websiteUrl }),
        });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        const data = await response.json();
        setMessage(data.message);
      } catch (error) {
        console.error('Error setting website URL:', error);
        setMessage('Failed to set website URL. Please try again.');
      }
    };

    return (
      <div style={styles.websiteInputContainer}>
        <h2>Set Website URL</h2>
        <form onSubmit={handleSubmit} style={styles.websiteInputForm}>
          <input
            type="text"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="Enter website URL"
            style={styles.websiteInput}
          />
          <button type="submit" style={styles.websiteInputButton}>
            Submit
          </button>
        </form>
        {message && <p style={styles.websiteInputMessage}>{message}</p>}
      </div>
    );
  };

  return (
    <Router>
      <Routes>
        {/* Main Chatbot Route */}
        <Route
          path="/"
          element={
            <div style={styles.container}>
              {/* Left Sidebar */}
              <div style={styles.sidebar}>
                <div style={styles.sidebarHeader}>
                  <img
                    src="https://res.cloudinary.com/deeobs5sb/image/upload/v1738443731/Logo-05_ysgntm.png" // Replace with the correct path to your logo file
                    alt="Logo"
                    style={styles.logo}
                  />
                  <span style={styles.sidebarTitle}>Rubitcube</span>
                </div>
                <div style={styles.sidebarOptions}>
                  <button style={styles.sidebarButton}>New Chat</button>
                  <button style={styles.sidebarButton}>Active Chats</button>
                </div>
              </div>

              {/* Main Chat Area */}
              <div style={styles.chatArea}>
                {/* Messages Container */}
                <div style={styles.messagesContainer}>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      style={{
                        ...styles.messageBubble,
                        ...(message.sender === 'bot' ? styles.botMessage : styles.userMessage),
                      }}
                    >
                      {message.text}
                      {message.typing && <span style={styles.typingIndicator}>...</span>}
                    </div>
                  ))}
                  {error && (
                    <div style={{ ...styles.messageBubble, ...styles.errorMessage }}>{error}</div>
                  )}
                </div>

                {/* Message Input Box */}
                <div style={styles.inputBoxContainer}>
                  <textarea
                    placeholder="Type your message..."
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    style={{
                      ...styles.inputBox,
                      height: `${Math.min(Math.max(inputText.split('\n').length * 20, 50), 200)}px`, // Flexible height
                    }}
                  />
                  <button onClick={handleSendMessage} style={styles.sendButton}>
                    Send
                  </button>
                </div>
              </div>
            </div>
          }
        />

        {/* Hidden Route for Website Input Form */}
        <Route path="/set-url" element={<WebsiteInput />} />
      </Routes>
    </Router>
  );
}

export default App;

// CSS Styles
const styles = {
  container: {
    display: 'flex',
    height: '100vh',
    backgroundColor: '#f7f7f7',
    fontFamily: 'Arial, sans-serif',
  },
  sidebar: {
    width: '250px',
    backgroundColor: '#2c3e50',
    color: '#fff',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  logo: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
  },
  sidebarTitle: {
    fontSize: '18px',
    fontWeight: '600',
  },
  sidebarOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  sidebarButton: {
    padding: '10px',
    backgroundColor: '#34495e',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '14px',
  },
  chatArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  messagesContainer: {
    width: '100%',
    maxWidth: '800px',
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '20px',
  },
  messageBubble: {
    maxWidth: '70%',
    padding: '12px 16px',
    borderRadius: '12px',
    fontSize: '16px',
    lineHeight: '1.5',
  },
  botMessage: {
    backgroundColor: '#f1f1f1',
    alignSelf: 'flex-start',
    borderBottomLeftRadius: '4px',
  },
  userMessage: {
    backgroundColor: '#007BFF',
    color: '#fff',
    alignSelf: 'flex-end',
    borderBottomRightRadius: '4px',
  },
  typingIndicator: {
    display: 'inline-block',
    marginLeft: '5px',
    animation: 'blink 1s infinite',
  },
  errorMessage: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    border: '1px solid #c62828',
  },
  inputBoxContainer: {
    width: '100%',
    maxWidth: '800px',
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-end',
  },
  inputBox: {
    flex: 1,
    padding: '12px 16px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    outline: 'none',
    resize: 'none', // Prevent manual resizing
    minHeight: '80px', // Minimum height
    maxHeight: '200px', // Maximum height
    overflowY: 'auto', // Scroll if content exceeds max height
  },
  sendButton: {
    padding: '12px 20px',
    fontSize: '16px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#007BFF',
    color: '#fff',
    cursor: 'pointer',
  },
  websiteInputContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    padding: '20px',
    backgroundColor: '#f7f7f7',
  },
  websiteInputForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
    maxWidth: '400px',
  },
  websiteInput: {
    padding: '12px 16px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    outline: 'none',
  },
  websiteInputButton: {
    padding: '12px 20px',
    fontSize: '16px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#007BFF',
    color: '#fff',
    cursor: 'pointer',
  },
  websiteInputMessage: {
    marginTop: '10px',
    color: '#333',
  },
};

// Add blinking animation for typing indicator
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(
  `@keyframes blink { 0% { opacity: 0; } 50% { opacity: 1; } 100% { opacity: 0; } }`,
  styleSheet.cssRules.length
);
